import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Paperclip, Send, Square } from 'lucide-react';
import { Button } from '../ui/Button.jsx';
import { Select } from '../ui/Select.jsx';
import { useSession } from '../../context/SessionContext';
import PersonaSelector from './PersonaSelector.jsx';

const DRAFT_PREFIX = 'etch.draft.';
const DRAFT_DEBOUNCE_MS = 300;
const MAX_HEIGHT_PX = 200;

export const SCOPE_STORAGE_KEY = 'etch.scope';

// Art. 50 Abs. 1/5 AI Act: say it is an AI before the first message, and tie
// the notice to the input so screen readers announce it on focus.
const AI_DISCLOSURE_ID = 'etch-composer-ai-disclosure';

function draftKey(conversationId) {
  return `${DRAFT_PREFIX}${conversationId || 'new'}`;
}

function readDraft(conversationId) {
  try {
    return localStorage.getItem(draftKey(conversationId)) || '';
  } catch {
    return '';
  }
}

/**
 * The composer (design spec §10.2, §11.1).
 *
 * Multiline, Enter sends, Shift+Enter breaks the line. The draft is persisted
 * per conversation under `etch.draft.<id>` and restored on mount, so neither a
 * failed request nor a reload can lose what the user typed.
 *
 * @param {object} props
 * @param {(question: string, context: null, options: {scope: string}) => void} props.onSubmit
 * @param {() => void} [props.onCancel]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.isStreaming]
 * @param {string} [props.placeholder]
 * @param {string|null} [props.conversationId]
 * @param {string|null} [props.fileContext] - the attached file context, if any.
 * @param {boolean} [props.offline]
 */
export function Composer({
  onSubmit,
  onCancel,
  disabled = false,
  isStreaming = false,
  placeholder,
  conversationId = null,
  fileContext = null,
  offline = false,
}) {
  const { t } = useTranslation();
  const { sessionMode } = useSession();
  const isAxiom = sessionMode === 'axiom';
  const textareaRef = useRef(null);
  const saveTimer = useRef(null);

  const [value, setValue] = useState(() => readDraft(conversationId));
  const [restored, setRestored] = useState(false);
  const [scope, setScope] = useState(() => {
    try {
      return localStorage.getItem(SCOPE_STORAGE_KEY) || 'all';
    } catch {
      return 'all';
    }
  });

  // Swap the draft when the thread changes — each conversation keeps its own.
  useEffect(() => {
    const draft = readDraft(conversationId);
    setValue(draft);
    setRestored(!!draft);
  }, [conversationId]);

  useEffect(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try {
        if (value) localStorage.setItem(draftKey(conversationId), value);
        else localStorage.removeItem(draftKey(conversationId));
      } catch {
        /* storage full or blocked — the draft simply is not durable then */
      }
    }, DRAFT_DEBOUNCE_MS);
    return () => clearTimeout(saveTimer.current);
  }, [value, conversationId]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_HEIGHT_PX)}px`;
  }, [value]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const changeScope = useCallback((next) => {
    setScope(next);
    try {
      localStorage.setItem(SCOPE_STORAGE_KEY, next);
    } catch { /* non-durable preference */ }
  }, []);

  const submit = (event) => {
    event?.preventDefault?.();
    const question = value.trim();
    if (!question || disabled) return;
    onSubmit?.(question, null, { scope });
    setValue('');
    setRestored(false);
    try {
      localStorage.removeItem(draftKey(conversationId));
    } catch { /* ignore */ }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey || !event.shiftKey)) {
      event.preventDefault();
      submit(event);
    }
    if (event.key === 'Escape' && isStreaming && onCancel) {
      event.preventDefault();
      onCancel();
    }
  };

  const textareaId = 'etch-composer-input';

  return (
    <form className="etch-composer" onSubmit={submit}>
      {fileContext && (
        <p className="etch-composer__attachments">
          <Paperclip size={13} aria-hidden="true" />
          <span>{t('chat.attachedContext', { name: fileContext.split('/').pop() })}</span>
        </p>
      )}

      {restored && (
        <p className="etch-composer__restored" role="status">{t('states.recovered')}</p>
      )}

      {offline && (
        <p className="etch-composer__offline" role="status">{t('states.offline')}</p>
      )}

      <label className="etch-visually-hidden" htmlFor={textareaId}>
        {t('chat.placeholder')}
      </label>
      <textarea
        id={textareaId}
        ref={textareaRef}
        className="etch-composer__input"
        placeholder={placeholder || (isAxiom ? t('chat.placeholderAxiom') : t('chat.placeholder'))}
        value={value}
        rows={1}
        onChange={(event) => { setValue(event.target.value); setRestored(false); }}
        onKeyDown={handleKeyDown}
        disabled={disabled && !isStreaming}
        aria-describedby={AI_DISCLOSURE_ID}
      />

      <div className="etch-composer__toolbar">
        <Select
          className="etch-composer__scope"
          label={t('chat.knowledgeScope')}
          hideLabel
          value={scope}
          onChange={(event) => changeScope(event.target.value)}
          options={[
            { value: 'company', label: t('chat.knowledgeScopeCompany') },
            { value: 'all', label: t('chat.knowledgeScopeAll') },
          ]}
        />
        <PersonaSelector />
        <span className="etch-composer__spacer" />
        {isStreaming ? (
          <Button variant="secondary" onClick={onCancel} data-action="stop">
            <Square size={14} aria-hidden="true" />
            <span>{t('chat.stop')}</span>
          </Button>
        ) : (
          <Button type="submit" variant="primary" disabled={!value.trim() || disabled} data-action="send">
            <Send size={14} aria-hidden="true" />
            <span>{t('chat.send')}</span>
          </Button>
        )}
      </div>

      <p className="etch-composer__disclosure" id={AI_DISCLOSURE_ID}>
        <Info className="etch-composer__disclosure-icon" size={13} aria-hidden="true" />
        <span>{t('chat.aiDisclosure')}</span>
      </p>

      <p className="etch-composer__hint">
        {t('chat.hint')}{isStreaming ? t('chat.hintStreaming') : ''}
      </p>
    </form>
  );
}

export default Composer;
